import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function Footer() {
  const { t } = useTranslation('homepage');
  const { ref, isVisible } = useScrollReveal<HTMLElement>(0.1);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navigateTo = (path: string) => {
    if (window.REACT_APP_NAVIGATE) {
      window.REACT_APP_NAVIGATE(path);
    }
  };

  const goHomeAndScroll = (id: string) => {
    navigateTo('/');
    setTimeout(() => scrollToSection(id), 300);
  };

  return (
    <footer
      ref={ref}
      className={`bg-slate-primary transition-all duration-1000 ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10 py-14 md:py-18">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-8">
          {/* Col 1: Brand + NAP */}
          <div className="lg:col-span-1">
            <h3 className="font-manrope font-extrabold text-white text-xl md:text-2xl mb-6">
              {t('footer_brand')}
            </h3>
            <div className="space-y-3 text-white/70 text-sm">
              <p className="flex items-start gap-2">
                <i className="ri-map-pin-line text-copper mt-0.5" />
                {t('footer_address')}
              </p>
              <a
                href="tel:+14184769950"
                aria-label="Appeler Couvreur-Lévis.com au 418-476-9950"
                title="Cliquez pour appeler"
                className="flex items-center gap-2 hover:text-white transition-colors"
              >
                <i className="ri-phone-line text-copper" />
                {t('phone_number')}
              </a>
              <a
                href="mailto:contact@couvreur-levis.com"
                className="flex items-center gap-2 hover:text-white transition-colors"
              >
                <i className="ri-mail-line text-copper" />
                {t('footer_email')}
              </a>
            </div>

            <div className="mt-6">
              <h4 className="text-white font-semibold text-sm mb-2">
                {t('footer_hours_title')}
              </h4>
              <p className="text-white/60 text-xs">{t('footer_hours_week')}</p>
              <p className="text-white/60 text-xs">{t('footer_hours_weekend')}</p>
            </div>
          </div>

          {/* Col 2: Services */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-5">
              {t('footer_services_title')}
            </h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => navigateTo('/refection-toiture-levis')}
                  className="text-white/70 text-sm hover:text-copper transition-colors"
                >
                  {t('footer_link_refaction')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateTo('/reparation-toiture-levis')}
                  className="text-white/70 text-sm hover:text-copper transition-colors"
                >
                  {t('footer_link_repair')}
                </button>
              </li>
          <li>
                <button
                  onClick={() => navigateTo('/bardeau-asphalte')}
                  className="text-white/70 text-sm hover:text-copper transition-colors"
                >
                  Bardeau d&rsquo;asphalte
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateTo('/inspection-toiture')}
                  className="text-white/70 text-sm hover:text-copper transition-colors"
                >
                  Inspection toiture
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateTo('/toit-plat-membrane-elastomere')}
                  className="text-white/70 text-sm hover:text-copper transition-colors"
                >
                  {t('footer_link_flat')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateTo('/toiture-tole-metal')}
                  className="text-white/70 text-sm hover:text-copper transition-colors"
                >
                  {t('footer_link_metal')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateTo('/deneigement-toiture')}
                  className="text-white/70 text-sm hover:text-copper transition-colors"
                >
                  Déneigement toiture
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateTo('/soumission-gratuite')}
                  className="text-white/70 text-sm hover:text-copper transition-colors"
                >
                  Soumission gratuite
                </button>
              </li>
              <li>
                <button
                  onClick={() => goHomeAndScroll('services')}
                  className="text-white/70 text-sm hover:text-copper transition-colors"
                >
                  {t('footer_link_inspection')}
                </button>
              </li>
            </ul>
          </div>

          {/* Col 3: Links */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-5">
              {t('footer_links_title')}
            </h4>
            <ul className="space-y-3">
              <li>
                <button
                  onClick={() => goHomeAndScroll('whyus')}
                  className="text-white/70 text-sm hover:text-copper transition-colors"
                >
                  {t('footer_link_about')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateTo('/a-propos')}
                  className="text-white/70 text-sm hover:text-copper transition-colors"
                >
                  À propos
                </button>
              </li>
              <li>
                <button
                  onClick={() => goHomeAndScroll('cta-final')}
                  className="text-white/70 text-sm hover:text-copper transition-colors"
                >
                  {t('footer_link_quote')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateTo('/mentions-legales')}
                  className="text-white/70 text-sm hover:text-copper transition-colors"
                >
                  {t('footer_link_legal')}
                </button>
              </li>
              <li>
                <button
                  onClick={() => navigateTo('/politique-confidentialite')}
                  className="text-white/70 text-sm hover:text-copper transition-colors"
                >
                  {t('footer_link_privacy')}
                </button>
              </li>
              <li>
                <span className="text-white/70 text-sm">{t('footer_link_blog')}</span>
              </li>
            </ul>
          </div>

          {/* Col 4: CTA */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-3">
              {t('cta_quote')}
            </h4>
            <p className="text-white/60 text-xs mb-4">
              {t('cta_subtitle')}
            </p>
            <button
              onClick={() => {
                const currentPath = window.location.pathname;
                if (currentPath === '/a-propos') {
                  navigateTo('/soumission-gratuite');
                } else if (currentPath === '/bardeau-asphalte' || currentPath === '/inspection-toiture' || currentPath === '/deneigement-toiture' || currentPath === '/soumission-gratuite') {
                  scrollToSection('quote-form');
                } else {
                  goHomeAndScroll('quote-form');
                }
              }}
              className="inline-flex items-center gap-2 bg-copper hover:bg-copper-dark text-white font-semibold px-5 py-3 rounded-md text-sm transition-all duration-300 whitespace-nowrap"
            >
              {t('cta_button')}
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <p className="text-white/50 text-xs md:text-sm text-center">
            {t('footer_copyright')}
          </p>
        </div>
      </div>
    </footer>
  );
}